import { CapacityCellRef, suggestContainerLine } from '@core/offers/offer-containers';
import { freightValuesPerM3, round6, SnapshotContainer } from './pricing';

/** One container entry of a proposal line snapshot (ProductOffer2ProposalContainer). */
export interface LineContainer {
  quantityTypeId: number;
  count: number;
  capacityM3: number;
  quantityType?: { id?: number; name?: string } | null;
}

/** The subset of AdditionalCost the freight dialogs need. `type` is 'frete' once ProposalService.products normalised it, 'FRETE' on raw backend data. */
export interface LineCostForFreight {
  id: number;
  type: string;
  basisQuantityTypeId?: number | null;
  freight?: {
    id?: number;
    company?: { name?: string } | null;
    freightType?: { quantityTypeId?: number | null } | null;
  } | null;
}

export interface LineForFreight {
  id?: number;
  /** CONTAINER (default, also when absent) or BULK. */
  transportMode?: 'CONTAINER' | 'BULK' | null;
  volumeM3?: number | null;
  containers?: LineContainer[] | null;
  additionalCosts?: LineCostForFreight[] | null;
}

export interface ContainerFreightBlock {
  quantityTypeId: number;
  typeName: string;
  count: number;
  capacityM3: number;
  existingCost: LineCostForFreight | null;
}

export interface ContainerFreightOptionView {
  freightId: number;
  company: string;
  destination: string;
  currency: string;
  /** Main-currency price of ONE container (Pricing.value). */
  containerPrice: number;
  count: number;
  capacityM3: number;
  /** containerPrice x count (what the line pays for this container type). */
  freightTotal: number;
  /** Preview of this freight's contribution to the line in currency per m3 (mirror of the backend derivation); null when it cannot be derived. */
  perM3Contribution: number | null;
  validity: string | null;
  expired: boolean;
}

export function todayISO(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** D13: expired means validity date < today (UTC calendar dates); a freight valid until today is still valid. */
export function isFreightExpired(validity: string | null | undefined, today: string): boolean {
  if (!validity) {
    return false;
  }
  return validity.slice(0, 10) < today;
}

export function mainPrice(freight: any): { value: number; currency: string } {
  const pricing = freight?.Pricing?.find((p: any) => p.isMainCurrency);
  return { value: pricing?.value ?? 0, currency: pricing?.currency?.name ?? 'EUR' };
}

function isFreightCost(cost: LineCostForFreight): boolean {
  return cost.type === 'frete' || cost.type === 'FRETE';
}

/**
 * Which container type of the line a FRETE cost belongs to: the stored basis type, else the type of the linked
 * freight (costs created before the basis existed), else the first container. null for non container lines.
 */
export function resolveCostContainerTypeId(
  line: LineForFreight | null | undefined,
  cost: LineCostForFreight | null | undefined,
): number | null {
  const containers = line?.containers ?? [];
  if (containers.length === 0) {
    return null;
  }
  const candidate = cost?.basisQuantityTypeId ?? cost?.freight?.freightType?.quantityTypeId ?? null;
  if (candidate != null && containers.some((c) => c.quantityTypeId === candidate)) {
    return candidate;
  }
  return containers[0].quantityTypeId;
}

export function buildContainerBlocks(line: LineForFreight | null | undefined): ContainerFreightBlock[] {
  const containers = line?.containers ?? [];
  const freightCosts = (line?.additionalCosts ?? []).filter(isFreightCost);
  return containers.map((container) => ({
    quantityTypeId: container.quantityTypeId,
    typeName: container.quantityType?.name ?? String(container.quantityTypeId),
    count: container.count,
    capacityM3: container.capacityM3,
    existingCost: freightCosts.find((cost) => resolveCostContainerTypeId(line, cost) === container.quantityTypeId) ?? null,
  }));
}

export function buildContainerFreightOption(
  freight: any,
  container: { quantityTypeId: number; count: number; capacityM3: number },
  containers: LineContainer[],
  volumeM3: number,
  today: string,
): ContainerFreightOptionView {
  const { value: containerPrice, currency } = mainPrice(freight);
  let perM3Contribution: number | null = null;
  try {
    const snapshot: SnapshotContainer[] = containers.map((c) => ({
      quantityTypeId: c.quantityTypeId,
      count: c.count,
      capacityM3: c.capacityM3,
    }));
    perM3Contribution =
      freightValuesPerM3([{ quantityTypeId: container.quantityTypeId, basisAmount: containerPrice }], snapshot, volumeM3)[
        container.quantityTypeId
      ] ?? null;
  } catch {
    // Unknown volume (0) or a negative price: the preview is simply unavailable, the backend stays authoritative.
    perM3Contribution = null;
  }
  return {
    freightId: freight.id,
    company: freight.company?.name ?? '',
    destination: freight.destinyPort?.name ?? '',
    currency,
    containerPrice,
    count: container.count,
    capacityM3: container.capacityM3,
    freightTotal: containerPrice * container.count,
    perM3Contribution,
    validity: freight.validity ?? null,
    expired: isFreightExpired(freight.validity, today),
  };
}

/** Max freights loaded per query in the proposal freight dialogs. */
export const FREIGHT_QUERY_TAKE = 200;

/** A destination port that has freights from the line origin port (FreightDestinationPorts). portId null = no destination. */
export interface FreightDestinationPort {
  portId: number | null;
  name: string | null;
  freightCount: number;
}

/**
 * FreightFindAll variables for the proposal freight dialogs: origin port, optionally the container type of a block (D13)
 * and the destination port picked in the dialog (null/undefined = "Todos").
 */
export function freightQuery(params: {
  originPortId: number | undefined;
  quantityTypeId?: number | null;
  destinyPortId?: number | null;
  /** Free text: case insensitive match on company, destination port, freight type, notes (ql) or continent. */
  search?: string | null;
  /** Bulk line: only BULK freight types (their tariff is per m3). */
  bulkOnly?: boolean;
  take?: number;
}) {
  const where: Record<string, unknown> = { originPortId: { equals: params.originPortId } };
  const freightType: Record<string, unknown> = {};
  if (params.quantityTypeId != null) {
    freightType.quantityTypeId = { equals: params.quantityTypeId };
  }
  if (params.bulkOnly) {
    freightType.name = { startsWith: 'BULK', mode: 'insensitive' };
  }
  if (Object.keys(freightType).length > 0) {
    where.freightType = { is: freightType };
  }
  if (params.destinyPortId != null) {
    where.destinyPortId = { equals: params.destinyPortId };
  }
  const search = params.search?.trim();
  if (search) {
    const contains = { contains: search, mode: 'insensitive' };
    where.OR = [
      { company: { is: { name: contains } } },
      { destinyPort: { is: { name: contains } } },
      { freightType: { is: { name: contains } } },
      { ql: contains },
      { continent: contains },
    ];
  }
  return { where, skip: 0, take: params.take ?? FREIGHT_QUERY_TAKE };
}

/**
 * Destination ports of already loaded freights (portId, name, count), by name. Fallback for an API without
 * FreightDestinationPorts: it only sees the loaded rows, so the counts can be short when the take was filled.
 */
export function destinationPortsFromFreights(
  rows: Array<{ destinyPortId?: number | null; destinyPort?: { id?: number; name?: string | null } | null }> | null | undefined,
): FreightDestinationPort[] {
  const byPort = new Map<number, FreightDestinationPort>();
  for (const row of rows ?? []) {
    const portId = row.destinyPortId ?? row.destinyPort?.id;
    if (portId == null) {
      continue;
    }
    const port = byPort.get(portId) ?? { portId, name: row.destinyPort?.name ?? null, freightCount: 0 };
    port.freightCount++;
    byPort.set(portId, port);
  }
  return [...byPort.values()].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
}

/**
 * Destination preselected in the freight dialogs: the request destination port when it has freights, else the only
 * option that is one of the proposal clients ports, else null ("Todos").
 */
export function defaultDestinationPortId(
  options: FreightDestinationPort[],
  requestDestinyPortId: number | null | undefined,
  clientPortIds: number[] | null | undefined,
): number | null {
  const ids = options.map((o) => o.portId).filter((id): id is number => id != null);
  if (requestDestinyPortId != null && ids.includes(+requestDestinyPortId)) {
    return +requestDestinyPortId;
  }
  const clientPorts = (clientPortIds ?? []).map(Number);
  const matches = ids.filter((id) => clientPorts.includes(id));
  return matches.length === 1 ? matches[0] : null;
}

/** A result that fills the take may have been cut: the dialog suggests filtering by destination port. */
export function mayHaveMoreFreights(rows: unknown[] | null | undefined, take: number = FREIGHT_QUERY_TAKE): boolean {
  return (rows?.length ?? 0) >= take;
}

/** BULK freight types (e.g. "BULK LOG", "BULK SWT") are quoted per m3 already. */
export function isBulkFreight(freight: any): boolean {
  return /^\s*BULK\b/i.test(freight?.freightType?.name ?? '');
}

/**
 * Non container lines (LCL, BULK, LTL) keep the manual EUR/m3 flow. The suggestion divides the flat tariff by the
 * line volume when known, except BULK whose tariff is already per m3 (used as is).
 * TODO(OQ5): the unit of LCL freight tariffs (per m3, per ton, per shipment) is unknown; the user can overwrite it.
 */
export function legacyFreightValuePerM3(flatPrice: number, volumeM3: number, freight?: any): number {
  if (isBulkFreight(freight)) {
    return flatPrice;
  }
  return volumeM3 > 0 ? flatPrice / volumeM3 : flatPrice;
}

/**
 * R2: the container type a freight implies through its Freight Type (freightType.quantityTypeId). null when the
 * freight type has no quantity type or that quantity type is not one of the container types (LCL, BULK, ...), in which
 * case the user is asked to pick the container type.
 */
export function resolveFreightContainerTypeId(freight: any, containerTypeIds: number[]): number | null {
  const raw = freight?.freightType?.quantityTypeId;
  if (raw == null || !Number.isFinite(+raw)) {
    return null;
  }
  return containerTypeIds.some((id) => +id === +raw) ? +raw : null;
}

export interface LegacyContainerPlan {
  quantityTypeId: number;
  capacityM3: number;
  /** R3: ceil(offer quantity / capacity), the minimum the volume needs. */
  needed: number;
  /** What the user chose (>= needed, integer). */
  count: number;
  totalCapacityM3: number;
  idleM3: number;
  /** Price of ONE container x count (main currency). */
  freightTotal: number;
  /** Volume billed: the offer volume, capped at what the chosen containers carry. */
  billedM3: number;
  /** Freight per m3 of the billed volume. */
  freightPerM3: number;
}

/**
 * A count is valid when it is an integer >= `min`. Callers pass 1: fewer containers than the volume needs are allowed
 * and bill only the volume they carry.
 */
export function isValidContainerCount(count: unknown, min: number): boolean {
  return typeof count === 'number' && Number.isInteger(count) && count >= min;
}

/**
 * Realtime ESTIMATE of freight on a legacy line moved to containers. The billed volume is the offer volume capped at the containers' capacity;
 * `count` null means the computed minimum. null when the volume or the capacity of the type is unknown. The backend
 * answer after the save is the authoritative one.
 */
export function legacyContainerPlan(
  offerVolumeM3: number,
  quantityTypeId: number | null,
  gridRowCells: CapacityCellRef[],
  containerPrice: number,
  count: number | null,
): LegacyContainerPlan | null {
  const suggestion = suggestContainerLine(offerVolumeM3, quantityTypeId, gridRowCells);
  if (!suggestion) {
    return null;
  }
  const chosen = count ?? suggestion.count;
  const totalCapacityM3 = round6(chosen * suggestion.capacityM3);
  const freightTotal = containerPrice * chosen;
  const billedM3 = Math.min(offerVolumeM3, totalCapacityM3);
  return {
    quantityTypeId: suggestion.quantityTypeId,
    capacityM3: suggestion.capacityM3,
    needed: suggestion.count,
    count: chosen,
    totalCapacityM3,
    idleM3: Math.max(0, round6(totalCapacityM3 - offerVolumeM3)),
    freightTotal,
    freightPerM3: freightTotal / billedM3,
    billedM3,
  };
}
